// The sentences the calendar's dialogs say, and the two rules that choose
// between them.
//
// The Android and iOS drawers ask the same questions in the same words. Which
// wording is MMS's, verbatim, and which is ours is argued at the component that
// renders it (`components/calendar/RecurringScopeDialog.jsx`,
// `components/calendar/ConflictDialog.jsx`,
// `components/calendar/event/RecurrenceSection.jsx`), which imports this.
//
// Dates arrive already formatted. The studio's date format is a locale
// reading (`useDateFormat`), and this file only places the text it is given.
//
// The sentences are the core's (`core/src/events.rs`: `message`,
// `clock_time`, `event_subtitle`, `conflict_line` and the rest). The two
// fixed tables below stay here because components read them at module scope,
// before the core is primed; `test/domain/calendarCore.test.js` holds them to
// the core's table word for word.
import { coreSync } from './core.js';

// The scope dialog, in its three uses. MMS says "entry/entries" when updating
// and "event/events" when deleting.
export const SCOPE_WORDING = Object.freeze({
  update: Object.freeze({
    title: 'Update Recurring Entries',
    confirm: 'Update',
    one: 'This entry only',
    following: 'This and following entries',
  }),
  recurrence: Object.freeze({
    title: 'Update Future Entries',
    confirm: 'Update',
    body: 'Changing the recurrence will update all future entries.',
  }),
  delete: Object.freeze({
    title: 'Delete Event',
    confirm: 'Delete',
    one: 'This event',
    following: 'This and following events',
    notices: true,
  }),
});

// The delete dialog's two notice channels, in MMS's words.
export const NOTICE_WORDING = Object.freeze({
  email: Object.freeze({
    label: 'Send email cancellation notices',
    students: 'Email students',
    parents: 'Email parents',
    warning: 'The following recipients have no email address on file. They will not receive notifications, but you can still delete the event.',
  }),
  sms: Object.freeze({
    label: 'Send SMS cancellation notices',
    students: 'SMS students',
    parents: 'SMS parents',
    warning: 'The following recipients have no SMS capable phone number on file. They will not receive notifications, but you can still delete the event.',
  }),
});

// "4:00 PM" from "16:00"; anything else comes back as it was.
export function clockTime(hhmm) {
  return coreSync().clockTime(hhmm == null || hhmm === '' ? null : String(hhmm));
}

// The line under "This event": "Lesson scheduled for 9/12/2026 at 4:00 PM".
// `dateText` is the date as the studio writes it, or empty to fall back to the
// ISO date itself; `timeText` the start time on the studio's clock (`16:00`
// in Germany, #534), or empty for the US clock.
export function eventSubtitle(detail, dateText = '', timeText = '') {
  if (!detail) return null;
  const text = (v) => (v ? String(v) : null);
  return coreSync().eventSubtitle(
    text(detail.date), String(dateText ?? ''), text(detail.categoryLabel), text(detail.startTime),
    String(timeText ?? ''), text(detail.zoneLabel),
  ) ?? null;
}

// The conflict dialog. The fixed sentences are a table, read at module scope,
// and pinned to the core's (`calendar_message`) by
// `test/domain/calendarCore.test.js`; the four that take a value are the
// core's own.
export const CONFLICT_WORDING = Object.freeze({
  title: 'Scheduling Conflicts Detected',
  body: 'There are conflicts with this scheduling change. Would you like to ignore the conflicts and continue, or cancel to undo the change?',
  confirm: 'Ignore & Continue',
  count: (n) => coreSync().conflictCountLine(Number(n)),
  line: (who, date) => coreSync().conflictLine([String(who)], null, String(date)),
  lineForYou: (date) => coreSync().conflictLine([], null, String(date)),
  horizon: (date) => coreSync().conflictHorizonLine(String(date)),
});

// The one line about one conflict: the students double-booked, by name, else
// the teacher, else the first-person sentence (#385).
export function conflictLine(conflict, { teacherName, studentName }, date) {
  const names = (conflict.doubleBooked?.studentIds || [])
    .map((id) => studentName?.(id))
    .map((n) => (n ? String(n) : ''));
  return coreSync().conflictLine(names, teacherName ? String(teacherName) : null, String(date));
}

// The help under "Temporary weekly hold", quoting the studio's own duration.
export function weeklyHoldHelp(days) {
  return coreSync().weeklyHoldHelp(Number(days));
}

// The warning under "Repeat Until" when a shorter run deletes entries.
export function deletingEntriesNotice(dateText) {
  return coreSync().deletingEntriesNotice(String(dateText));
}
